import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { TarotCard } from './TarotCard';
import { CelticCrossLayout } from './CelticCrossLayout';
import { Button, Card, Loading } from '../common';
import { ExportService } from '../../services/exportService';
import { TAROT_DECK } from '../../data/tarotDeck';
import { spreads } from '../../data/spreads';
import type { DrawnCard, TarotSpread } from '../../types/tarot';
import { generateBasicInterpretation } from '../../utils/tarotLogic';
import { ExportMenu } from './ExportMenu';

export function SharedReading() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [reading, setReading] = useState<{
    question: string;
    cards: DrawnCard[];
    spread: TarotSpread;
    interpretation: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSharedReading = () => {
      try {
        const encodedData = searchParams.get('data');
        if (!encodedData) {
          setError('No reading data found in URL');
          setLoading(false);
          return;
        }

        const decodedData = ExportService.decodePermalinkData(encodedData);
        if (!decodedData) {
          setError('Invalid reading data');
          setLoading(false);
          return;
        }

        // Find the spread
        const spread = spreads.find(s => s.id === decodedData.spreadId);
        if (!spread) {
          setError('Unknown spread type');
          setLoading(false);
          return;
        }

        // Reconstruct the cards
        const cards: DrawnCard[] = decodedData.cards.map(cardData => {
          const baseCard = TAROT_DECK.find((card: any) => card.id === cardData.id);
          if (!baseCard) {
            throw new Error(`Card not found: ${cardData.id}`);
          }

          const position = spread.positions[cardData.position - 1];
          
          return {
            ...baseCard,
            position: cardData.position,
            isReversed: cardData.isReversed,
            positionMeaning: position?.description || `Position ${cardData.position}`
          };
        });

        // Generate interpretation
        const interpretation = generateBasicInterpretation(cards, spread, decodedData.question);

        setReading({
          question: decodedData.question,
          cards,
          spread,
          interpretation
        });
      } catch (error) {
        console.error('Error loading shared reading:', error);
        setError('Failed to load reading');
      } finally {
        setLoading(false);
      }
    };

    loadSharedReading();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (error || !reading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <Card className="max-w-md mx-auto text-center">
          <div className="p-6">
            <h2 className="text-xl font-bold text-white mb-4">
              Unable to Load Reading
            </h2>
            <p className="text-gray-400 mb-6">
              {error || 'The shared reading could not be loaded.'}
            </p>
            <Button onClick={() => navigate('/')}>
              Go to Home
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div id="shared-reading-content" className="space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              Shared Tarot Reading
            </h1>
            <p className="text-gray-400 mb-4">
              "{reading.question}"
            </p>
            <div className="text-sm text-primary-500">
              {reading.spread.name} • {reading.cards.length} cards
            </div>
          </div>

          {/* Cards Display */}
          <Card className="relative min-h-96 bg-dark-800 overflow-hidden">
            <div className="relative h-full p-4 md:p-8">
              {reading.spread.id === 'celtic-cross' ? (
                <CelticCrossLayout
                  cards={reading.cards}
                  spread={reading.spread}
                  revealedCards={new Set(reading.cards.map((_, i) => i))}
                  showMeanings={true}
                />
              ) : (
                reading.cards.map((card, index) => {
                  const position = reading.spread.positions[index];
                  
                  return (
                    <div
                      key={`${card.id}-${index}`}
                      className="absolute"
                      style={{
                        left: `${position.x}%`,
                        top: `${position.y}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    >
                      <div className="text-center">
                        <TarotCard
                          card={card}
                          isRevealed={true}
                          size="md"
                          showMeaning={true}
                          enableHoverEffects={true}
                          position={index + 1}
                          spreadType={reading.spread.id}
                        />
                        <div className="mt-2 space-y-1">
                          <div className="text-xs font-semibold text-white">
                            {position.name}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Card Details */}
          <Card className="bg-dark-800">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Card Details</h3>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {reading.cards.map((card, index) => {
                  const position = reading.spread.positions[index];
                  const meanings = card.isReversed ? card.meanings.reversed : card.meanings.upright;
                  
                  return (
                    <div key={`${card.id}-${index}`} className="bg-dark-700 p-4 rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-primary-500 font-bold text-sm">
                          {index + 1}
                        </span>
                        <h4 className="font-semibold text-white">
                          {card.name}
                          {card.isReversed && (
                            <span className="text-red-400 ml-1">(Reversed)</span>
                          )}
                        </h4>
                      </div>
                      
                      <p className="text-xs text-gray-400 mb-2">
                        {position.name}
                      </p>
                      
                      <div className="text-sm text-gray-300">
                        {meanings.slice(0, 2).map((meaning, i) => (
                          <div key={i} className="mb-1">• {meaning}</div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Interpretation */}
          <Card className="bg-dark-800">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Interpretation</h3>
              <div className="whitespace-pre-line text-gray-300 text-sm leading-relaxed">
                {reading.interpretation}
              </div>
            </div>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <ExportMenu 
              reading={reading}
              elementId="shared-reading-content"
            />
            
            <Button
              variant="secondary"
              onClick={() => navigate('/')}
            >
              Create Your Own Reading
            </Button>
          </div>

          {/* Footer */}
          <div className="text-center text-xs text-gray-500 pt-4">
            <p>Generated with Tarot Card Reader PWA</p>
            <p className="mt-1">
              This is a shared reading. Create your own personalized readings at{' '}
              <button
                onClick={() => navigate('/')}
                className="text-primary-400 hover:text-primary-300 underline"
              >
                our app
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}